import React, { useMemo, useState } from "react";
import { Box, Paper, Stack, Tooltip, Typography, alpha } from "@mui/material";

const CELL_SIZE = 78;
const CELL_GAP = 8;

// El plano dibuja racks, no huecos. Todas las posiciones de un mismo rack
// comparten layout_x/layout_y, así que la celda es la unidad natural: dibujar
// cada hueco daría un plano ilegible y además mentiría, porque están uno encima
// del otro. Los niveles y posiciones se ven al abrir el rack (vista de alzado).
export const groupBinsIntoRacks = (bins = []) => {
    const cells = new Map();

    bins.forEach((bin) => {
        if (bin.layout_x === null || bin.layout_y === null) return;

        const key = `${bin.layout_x}|${bin.layout_y}`;
        if (!cells.has(key)) {
            cells.set(key, {
                key,
                x: bin.layout_x,
                y: bin.layout_y,
                bins: [],
            });
        }
        cells.get(key).bins.push(bin);
    });

    return Array.from(cells.values()).map((cell) => {
        const occupied = cell.bins.filter((bin) => bin.sku_count > 0).length;
        const sequences = cell.bins.map((bin) => bin.pick_sequence).filter((seq) => seq !== null && seq !== undefined);
        const rackCount = new Set(cell.bins.map(rackIdentity)).size;

        return {
            ...cell,
            total: cell.bins.length,
            occupied,
            occupancy: cell.bins.length ? occupied / cell.bins.length : 0,
            fromErp: cell.bins.some((bin) => bin.source === "erp"),
            unstructured: cell.bins.some((bin) => !bin.structured),
            minSequence: sequences.length ? Math.min(...sequences) : null,
            // El nombre corto del rack: la parte del código común a sus posiciones.
            label: rackLabel(cell.bins),
            // Huella del rack en celdas. Un rack largo ocupa varias columnas del
            // plano y su celda se dibuja del ancho que le corresponde.
            w: Math.max(1, ...cell.bins.map((bin) => bin.layout_w || 1)),
            h: Math.max(1, ...cell.bins.map((bin) => bin.layout_h || 1)),
            rotation: cell.bins[0]?.rotation || 0,
            zoneId: cell.bins[0]?.warehouse_zone_id ?? null,
            // Más de un rack en la misma celda. No debería pasar — el generador
            // corre el pasillo para evitarlo y el arrastre no deja apilar — pero
            // una planilla puede traer dos racks con las mismas coordenadas. Sin
            // avisarlo, la celda se ve como un rack solo con el doble de
            // posiciones y los niveles repetidos, que es una bodega que no existe.
            rackCount,
            stacked: rackCount > 1,
        };
    });
};

// Identidad física de un rack: zona + pasillo + rack. Dos posiciones con la
// misma identidad son el mismo mueble; el nivel y el hueco viven dentro.
const rackIdentity = (bin) => [bin.zone, bin.aisle, bin.rack].map((part) => part ?? "").join("|");

// "A-01-03-B-02" y "A-01-03-A-01" comparten "A-01-03": ese es el rack. Se corta
// en el último segmento común para no repetir nivel y posición en la etiqueta.
const rackLabel = (bins) => {
    if (!bins.length) return "";
    const segments = bins.map((bin) => String(bin.code || "").split("-"));
    const shortest = Math.min(...segments.map((parts) => parts.length));

    const common = [];
    for (let i = 0; i < shortest; i += 1) {
        const value = segments[0][i];
        if (segments.every((parts) => parts[i] === value)) {
            common.push(value);
        } else {
            break;
        }
    }

    return common.length ? common.join("-") : bins[0].code;
};

const cellColor = (cell, mode, theme, sequenceRange, zonesById) => {
    const empty = theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[200];

    if (mode === "zonas") {
        const color = cell.zoneId && zonesById?.get(cell.zoneId)?.color;
        if (color) return alpha(color, 0.5);
        // Sin zona asignada no se inventa un color: se ve que falta.
        return empty;
    }

    if (mode === "vacias") {
        return cell.occupied === 0 ? alpha(theme.palette.warning.main, 0.55) : empty;
    }

    if (mode === "erp") {
        return cell.fromErp ? alpha(theme.palette.info.main, 0.55) : empty;
    }

    if (mode === "recorrido") {
        if (cell.minSequence === null || !sequenceRange) return empty;
        const { min, max } = sequenceRange;
        const ratio = max === min ? 0 : (cell.minSequence - min) / (max - min);
        // Del inicio del recorrido (claro) al final (fuerte).
        return alpha(theme.palette.secondary.main, 0.15 + ratio * 0.65);
    }

    // Por defecto: ocupación.
    if (cell.occupancy === 0) return empty;
    return alpha(theme.palette.primary.main, 0.2 + cell.occupancy * 0.6);
};

const BoardPlan = ({
    bins,
    bounds,
    colorMode,
    onSelectRack,
    onMoveRack,
    editable,
    zones = [],
    // Pieza elegida en la paleta. Mientras haya una, las celdas libres se
    // convierten en destinos: se hace clic y se estampa el rack ahí.
    placingTemplate = null,
    placingRotation = 0,
    onPlaceAt,
}) => {
    const [draggingKey, setDraggingKey] = useState(null);
    const [hoverCell, setHoverCell] = useState(null);

    const racks = useMemo(() => groupBinsIntoRacks(bins), [bins]);

    const sequenceRange = useMemo(() => {
        const sequences = racks.map((rack) => rack.minSequence).filter((seq) => seq !== null);
        if (!sequences.length) return null;
        return { min: Math.min(...sequences), max: Math.max(...sequences) };
    }, [racks]);

    // Se deja un margen de una celda alrededor para poder arrastrar un rack
    // fuera del área ocupada y ampliar el plano.
    const minX = (bounds?.min_x ?? 0) - 1;
    const minY = (bounds?.min_y ?? 0) - 1;
    const maxX = (bounds?.max_x ?? 0) + 1;
    const maxY = (bounds?.max_y ?? 0) + 1;

    const columns = Math.max(1, maxX - minX + 1);
    const rows = Math.max(1, maxY - minY + 1);

    const rackByKey = useMemo(() => {
        const map = new Map();
        racks.forEach((rack) => map.set(`${rack.x}|${rack.y}`, rack));
        return map;
    }, [racks]);

    const zonesById = useMemo(() => new Map(zones.map((zone) => [zone.id, zone])), [zones]);

    // Toda celda que cae bajo la huella de un rack, apuntando a su ancla. Un rack
    // de 3×1 bloquea tres celdas: sin esto, las otras dos se dibujarían como
    // libres encima del propio rack.
    const coveredByKey = useMemo(() => {
        const map = new Map();
        racks.forEach((rack) => {
            for (let dx = 0; dx < rack.w; dx += 1) {
                for (let dy = 0; dy < rack.h; dy += 1) {
                    map.set(`${rack.x + dx}|${rack.y + dy}`, rack.key);
                }
            }
        });
        return map;
    }, [racks]);

    // ¿Cabe una huella de w×h en (x,y) sin pisar otro rack? El propio rack que se
    // está moviendo no cuenta como obstáculo.
    const fits = (x, y, w, h, ignoreKey = null) => {
        for (let dx = 0; dx < w; dx += 1) {
            for (let dy = 0; dy < h; dy += 1) {
                const owner = coveredByKey.get(`${x + dx}|${y + dy}`);
                if (owner && owner !== ignoreKey) return false;
            }
        }
        return true;
    };

    const placingSize = useMemo(() => {
        if (!placingTemplate) return { w: 1, h: 1 };
        const swap = placingRotation === 90 || placingRotation === 270;
        return {
            w: swap ? placingTemplate.footprint_h : placingTemplate.footprint_w,
            h: swap ? placingTemplate.footprint_w : placingTemplate.footprint_h,
        };
    }, [placingTemplate, placingRotation]);

    if (!racks.length) {
        return (
            <Paper variant="outlined" sx={{ p: 6, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary">
                    Esta bodega todavía no tiene posiciones ubicadas en el plano
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Genera un pasillo para empezar, o importa tus posiciones desde Excel.
                </Typography>
            </Paper>
        );
    }

    const handleDrop = (x, y) => {
        setHoverCell(null);
        if (!draggingKey) return;

        const rack = rackByKey.get(draggingKey);
        setDraggingKey(null);
        if (!rack) return;
        if (rack.x === x && rack.y === y) return;
        // No se apilan dos racks en la misma celda: el plano dejaría de
        // representar la bodega. Se mira la huella completa, no solo el ancla.
        if (!fits(x, y, rack.w, rack.h, rack.key)) return;

        onMoveRack(rack, x, y);
    };

    return (
        <Box sx={{ overflow: "auto", pb: 2 }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${columns}, ${CELL_SIZE}px)`,
                    gridTemplateRows: `repeat(${rows}, ${CELL_SIZE}px)`,
                    gap: `${CELL_GAP}px`,
                    width: "max-content",
                }}
            >
                {Array.from({ length: rows }).map((_, rowIndex) =>
                    Array.from({ length: columns }).map((__, colIndex) => {
                        const x = minX + colIndex;
                        const y = minY + rowIndex;
                        const key = `${x}|${y}`;
                        const rack = rackByKey.get(key);
                        const isHover = hoverCell === key;
                        // Celda tapada por la huella de un rack vecino: no se
                        // dibuja nada, el rack ya ocupa este lugar.
                        const covered = coveredByKey.get(key);
                        const cellPlacement = {
                            gridColumn: `${colIndex + 1} / span ${rack ? rack.w : 1}`,
                            gridRow: `${rowIndex + 1} / span ${rack ? rack.h : 1}`,
                        };

                        if (!rack) {
                            if (covered) return null;

                            const canPlace = Boolean(placingTemplate) &&
                                fits(x, y, placingSize.w, placingSize.h);

                            return (
                                <Box
                                    key={`empty-${x}-${y}`}
                                    onDragOver={(event) => {
                                        if (!editable || !draggingKey) return;
                                        event.preventDefault();
                                        setHoverCell(key);
                                    }}
                                    onDragLeave={() => setHoverCell(null)}
                                    onDrop={() => handleDrop(x, y)}
                                    onMouseEnter={() => canPlace && setHoverCell(key)}
                                    onMouseLeave={() => canPlace && setHoverCell(null)}
                                    onClick={() => canPlace && onPlaceAt?.(x, y)}
                                    sx={{
                                        ...cellPlacement,
                                        borderRadius: 1,
                                        border: "1px dashed",
                                        cursor: canPlace ? "copy" : "default",
                                        borderColor: isHover
                                            ? "primary.main"
                                            : canPlace
                                            ? "divider"
                                            : "transparent",
                                        bgcolor: isHover ? "action.hover" : "transparent",
                                    }}
                                />
                            );
                        }

                        return (
                            <Tooltip
                                key={rack.key}
                                arrow
                                title={
                                    <Box>
                                        <Typography variant="subtitle2">{rack.label}</Typography>
                                        <Typography variant="caption" display="block">
                                            {rack.occupied} de {rack.total} posiciones ocupadas
                                        </Typography>
                                        {rack.minSequence !== null && (
                                            <Typography variant="caption" display="block">
                                                Recorrido: #{rack.minSequence}
                                            </Typography>
                                        )}
                                        {rack.zoneId && zonesById.get(rack.zoneId) && (
                                            <Typography variant="caption" display="block">
                                                Zona {zonesById.get(rack.zoneId).code} ·{" "}
                                                {zonesById.get(rack.zoneId).name}
                                            </Typography>
                                        )}
                                        {rack.fromErp && (
                                            <Typography variant="caption" display="block">
                                                Viene del ERP
                                            </Typography>
                                        )}
                                        {rack.stacked && (
                                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                ⚠ {rack.rackCount} racks distintos en esta celda. Arrastra uno a otra
                                                celda para separarlos.
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            >
                                <Paper
                                    elevation={0}
                                    draggable={editable}
                                    onDragStart={() => setDraggingKey(rack.key)}
                                    onDragEnd={() => {
                                        setDraggingKey(null);
                                        setHoverCell(null);
                                    }}
                                    onClick={() => onSelectRack(rack)}
                                    sx={(theme) => ({
                                        ...cellPlacement,
                                        cursor: "pointer",
                                        p: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        border: "1px solid",
                                        borderColor: rack.stacked ? "warning.main" : "divider",
                                        borderRadius: 1,
                                        opacity: draggingKey === rack.key ? 0.4 : 1,
                                        bgcolor: cellColor(rack, colorMode, theme, sequenceRange, zonesById),
                                        transition: "transform .12s",
                                        "&:hover": { transform: "scale(1.04)" },
                                    })}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 600, lineHeight: 1.2, wordBreak: "break-all" }}
                                    >
                                        {rack.label}
                                    </Typography>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                                        <Typography variant="caption" color="text.secondary">
                                            {rack.occupied}/{rack.total}
                                        </Typography>
                                        {rack.unstructured && (
                                            <Typography variant="caption" color="warning.main" title="Sin estructura">
                                                !
                                            </Typography>
                                        )}
                                    </Stack>
                                </Paper>
                            </Tooltip>
                        );
                    })
                )}
            </Box>
        </Box>
    );
};

export default BoardPlan;
